<template>
  <div>
    <v-row class="mt-6">
      <!-- Customer Purchase Orders Card -->
      <v-col cols="12" md="6" lg="3">
        <v-card class="stat-card" elevation="3" color="blue darken-4" dark>
          <v-card-text>
            <div class="d-flex align-center mb-3">
              <v-icon large class="mr-3">mdi-cart-outline</v-icon>
              <div>
                <div class="text-h6">Government PO</div>
                <div class="text-caption">Purchase Orders</div>
              </div>
            </div>

            <v-divider class="mb-3"></v-divider>

            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-label">Total</div>
                <div class="stat-value">
                  {{ customer_purchase_orders.length }}
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Pending</div>
                <div class="stat-value warning--text">
                  {{ getPendingCount(customer_purchase_orders) }}
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Completed</div>
                <div class="stat-value success--text">
                  {{ getCompletedCount(customer_purchase_orders) }}
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Total Amount</div>
                <div class="stat-value text-subtitle-2">
                  ₱{{ formatAmount(getTotalAmount(customer_purchase_orders)) }}
                </div>
              </div>
            </div>

            <v-btn
              block
              outlined
              small
              class="mt-4"
              @click="navigateTo('/customer_purchase_orders')"
            >
              <v-icon small left>mdi-arrow-right</v-icon>
              View All
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Supplier Purchase Orders Card -->
      <v-col cols="12" md="6" lg="3">
        <v-card class="stat-card" elevation="3" color="orange darken-3" dark>
          <v-card-text>
            <div class="d-flex align-center mb-3">
              <v-icon large class="mr-3">mdi-truck-delivery-outline</v-icon>
              <div>
                <div class="text-h6">Supplier PO</div>
                <div class="text-caption">Purchase Orders</div>
              </div>
            </div>

            <v-divider class="mb-3"></v-divider>

            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-label">Total</div>
                <div class="stat-value">
                  {{ supplier_purchase_orders.length }}
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Pending</div>
                <div class="stat-value warning--text">
                  {{ getSupplierPOPendingCount() }}
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Completed</div>
                <div class="stat-value success--text">
                  {{ getSupplierPOCompletedCount() }}
                </div>
              </div>
              <div class="stat-item">
                <div class="stat-label">Total Amount</div>
                <div class="stat-value text-subtitle-2">
                  ₱{{ formatAmount(getTotalAmount(supplier_purchase_orders)) }}
                </div>
              </div>
            </div>

            <v-btn
              block
              outlined
              small
              class="mt-4"
              @click="navigateTo('/supplier_purchase_orders')"
            >
              <v-icon small left>mdi-arrow-right</v-icon>
              View All
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recent Activity Summary -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-clock-outline</v-icon>
            <span>Recent Procurement Activity</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="refreshData" :loading="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <div class="text-h6 mb-2">
                  <v-icon small color="warning" class="mr-2"
                    >mdi-alert-circle</v-icon
                  >
                  Items Requiring Attention
                </div>
                <v-list dense>
                  <v-list-item
                    v-if="getPendingCount(customer_purchase_orders) > 0"
                  >
                    <v-list-item-icon>
                      <v-icon color="blue">mdi-cart-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ getPendingCount(customer_purchase_orders) }} Pending
                        Government PO
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="getSupplierPOPendingCount() > 0">
                    <v-list-item-icon>
                      <v-icon color="orange">mdi-truck-delivery-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ getSupplierPOPendingCount() }} Pending Supplier PO
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="getTotalPendingCount() === 0">
                    <v-list-item-icon>
                      <v-icon color="success">mdi-check-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="success--text">
                        All items are up to date!
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-h6 mb-2">
                  <v-icon small color="success" class="mr-2"
                    >mdi-chart-line</v-icon
                  >
                  Quick Stats
                </div>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title
                        >Total Procurement Value</v-list-item-title
                      >
                      <v-list-item-subtitle class="text-h6 primary--text">
                        ₱{{ formatAmount(getTotalProcurementValue()) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Total Documents</v-list-item-title>
                      <v-list-item-subtitle class="text-h6">
                        {{ getTotalDocuments() }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Approval Rate</v-list-item-title>
                      <v-list-item-subtitle
                        class="text-h6"
                        :class="getApprovalRateColor()"
                      >
                        {{ getApprovalRate() }}%
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "ProcurementSummary",

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapGetters({
      all_customer_purchase_orders:
        "customer_purchase_order/customer_purchase_orders",
      all_supplier_purchase_orders: "purchase_order/purchase_orders",
      user: "auth/user",
    }),

    // Filter customer purchase orders by branch
    customer_purchase_orders() {
      if (!this.user || !this.user.branch_id) {
        return this.all_customer_purchase_orders;
      }
      return this.all_customer_purchase_orders.filter((po) => {
        return po.branch_id === this.user.branch_id;
      });
    },

    // Filter supplier purchase orders by branch
    supplier_purchase_orders() {
      if (!this.user || !this.user.branch_id) {
        return this.all_supplier_purchase_orders;
      }
      return this.all_supplier_purchase_orders.filter((po) => {
        // Check direct branch_id
        if (po.branch_id) {
          return po.branch_id === this.user.branch_id;
        }
        // Fallback to canvass -> customer_purchase_order branch_id
        if (po.canvass && po.canvass.customer_purchase_order) {
          return (
            po.canvass.customer_purchase_order.branch_id === this.user.branch_id
          );
        }
        return false;
      });
    },
  },

  methods: {
    ...mapActions({
      get_customer_purchase_orders:
        "customer_purchase_order/get_customer_purchase_orders",
      get_supplier_purchase_orders: "purchase_order/get_purchase_orders",
    }),

    async refreshData() {
      this.loading = true;
      try {
        await Promise.all([
          this.get_customer_purchase_orders(),
          this.get_supplier_purchase_orders(),
        ]);
      } catch (error) {
        console.error("Error refreshing dashboard data:", error);
      } finally {
        this.loading = false;
      }
    },

    getPendingCount(items) {
      return items.filter((item) => item.status === "Pending").length;
    },

    getCompletedCount(items) {
      return items.filter((item) => item.status === "Completed").length;
    },

    // Supplier PO uses "is_completed" boolean field
    getSupplierPOPendingCount() {
      return this.supplier_purchase_orders.filter((item) => !item.is_completed)
        .length;
    },

    getSupplierPOCompletedCount() {
      return this.supplier_purchase_orders.filter(
        (item) => item.is_completed === true
      ).length;
    },

    getTotalAmount(items) {
      return items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    },

    getTotalPendingCount() {
      return (
        this.getPendingCount(this.customer_purchase_orders) +
        this.getSupplierPOPendingCount()
      );
    },

    getTotalDocuments() {
      return (
        this.customer_purchase_orders.length +
        this.supplier_purchase_orders.length
      );
    },

    getTotalProcurementValue() {
      return (
        this.getTotalAmount(this.customer_purchase_orders) +
        this.getTotalAmount(this.supplier_purchase_orders)
      );
    },

    getApprovalRate() {
      const total = this.getTotalDocuments();
      if (total === 0) return 0;

      const approved =
        this.getCompletedCount(this.customer_purchase_orders) +
        this.getSupplierPOCompletedCount();

      return ((approved / total) * 100).toFixed(1);
    },

    getApprovalRateColor() {
      const rate = parseFloat(this.getApprovalRate());
      if (rate >= 80) return "success--text";
      if (rate >= 50) return "warning--text";
      return "error--text";
    },

    formatAmount(amount) {
      return parseFloat(amount || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    navigateTo(route) {
      this.$router.push(route);
    },
  },

  async mounted() {
    await this.refreshData();
  },
};
</script>

<style scoped>
.stat-card {
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3) !important;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.stat-item {
  text-align: center;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.9;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
